import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { DismissIcon } from "../../primitives/Icons/Icons";
import css from "./Popover.module.css";

const PopoverRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  modal = false,
  children,
  ...rest
}) => (
  <BasePopover.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    onOpenChangeComplete={onOpenChangeComplete}
    modal={modal}
    {...rest}
  >
    {children}
  </BasePopover.Root>
);

PopoverRoot.displayName = "PopoverRoot";

PopoverRoot.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onOpenChangeComplete: PropTypes.func,
  modal: PropTypes.oneOf([true, false, "trap-focus"]),
  children: PropTypes.node,
};

const PopoverTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BasePopover.Trigger ref={ref} className={className} {...rest}>
      {children}
    </BasePopover.Trigger>
  ),
);

PopoverTrigger.displayName = "PopoverTrigger";

PopoverTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const PopoverPopup = React.forwardRef(
  (
    {
      side = "bottom",
      sideOffset = 8,
      align = "center",
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BasePopover.Portal>
      <BasePopover.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        className={css.positioner}
      >
        <BasePopover.Popup
          ref={ref}
          className={clsx(css.popup, className)}
          {...rest}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  ),
);

PopoverPopup.displayName = "PopoverPopup";

PopoverPopup.propTypes = {
  side: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  sideOffset: PropTypes.number,
  align: PropTypes.oneOf(["start", "center", "end"]),
  className: PropTypes.string,
  children: PropTypes.node,
};

const PopoverArrow = React.forwardRef(({ className, ...rest }, ref) => (
  <BasePopover.Arrow
    ref={ref}
    className={clsx(css.arrow, className)}
    {...rest}
  />
));

PopoverArrow.displayName = "PopoverArrow";

PopoverArrow.propTypes = {
  className: PropTypes.string,
};

const PopoverTitle = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BasePopover.Title
      ref={ref}
      className={clsx(css.title, className)}
      {...rest}
    >
      {children}
    </BasePopover.Title>
  ),
);

PopoverTitle.displayName = "PopoverTitle";

PopoverTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const PopoverDescription = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BasePopover.Description
      ref={ref}
      className={clsx(css.description, className)}
      {...rest}
    >
      {children}
    </BasePopover.Description>
  ),
);

PopoverDescription.displayName = "PopoverDescription";

PopoverDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const PopoverClose = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BasePopover.Close
      ref={ref}
      aria-label="Close"
      className={clsx(css.close, className)}
      {...rest}
    >
      {children || <DismissIcon />}
    </BasePopover.Close>
  ),
);

PopoverClose.displayName = "PopoverClose";

PopoverClose.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Popup: PopoverPopup,
  Arrow: PopoverArrow,
  Title: PopoverTitle,
  Description: PopoverDescription,
  Close: PopoverClose,
};

export default Popover;
